import React, { useCallback, useEffect, useState } from "react";
import { Alert, Button, Col, Input, Label, Row, Spinner, Table } from "reactstrap";
import { toast } from "react-toastify";
import AppModal from "../../../../Components/Common/AppModal";
import {
  envListMasterSystemServer,
  envSetMasterSystemServer,
  envDeleteMasterSystemServer,
} from "../../../../helpers/fakebackend_helper";

// App-level environment variables on a server's DigitalOcean app
// (Change #100749). DigitalOcean only takes a whole app spec, so every save or
// remove rewrites the spec server side and redeploys each chosen app.

const SCOPES = [
  { value: "RUN_AND_BUILD_TIME", label: "Run and build time" },
  { value: "RUN_TIME", label: "Run time" },
  { value: "BUILD_TIME", label: "Build time" },
];

const emptyForm = { Key: "", Value: "", Scope: "RUN_AND_BUILD_TIME", Type: "GENERAL" };

const EnvVarsModal = ({ open, onClose, server, servers, onApplied }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [envList, setEnvList] = useState([]);
  const [componentEnvCount, setComponentEnvCount] = useState(0);
  const [form, setForm] = useState(emptyForm);
  const [editingKey, setEditingKey] = useState(null);
  const [confirmRemoveKey, setConfirmRemoveKey] = useState(null);
  // other servers the change is also applied to
  const [alsoServerIds, setAlsoServerIds] = useState([]);
  const [results, setResults] = useState([]);

  const otherServers = (servers || []).filter((s) => s.Id !== server?.Id);

  const loadEnvs = useCallback(async () => {
    if (!server?.Id) return;
    setLoading(true);
    setError(null);
    try {
      const res = await envListMasterSystemServer({ id: server.Id });
      if (res?.status === 200) {
        setEnvList(Array.isArray(res.env_list) ? res.env_list : []);
        setComponentEnvCount(Number(res.component_env_count) || 0);
      } else {
        setEnvList([]);
        setError(res?.message || "Could not load variables");
      }
    } catch (e) {
      setError("Could not load variables");
    }
    setLoading(false);
  }, [server]);

  useEffect(() => {
    if (!open) return;
    setForm(emptyForm);
    setEditingKey(null);
    setConfirmRemoveKey(null);
    setAlsoServerIds([]);
    setResults([]);
    loadEnvs();
  }, [open, loadEnvs]);

  const targetIds = () => [server.Id, ...alsoServerIds];

  const finish = (res, okMessage) => {
    setResults(Array.isArray(res?.results) ? res.results : []);
    if (res?.status === 200) {
      toast.success(res?.message || okMessage);
    } else {
      toast.error(res?.message || "Change failed");
    }
    const applied = (res?.results || []).filter((r) => Number(r.Success)).map((r) => r.Id);
    if (applied.length && onApplied) onApplied(applied);
    loadEnvs();
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await envSetMasterSystemServer({
        Servers: targetIds(),
        Key: form.Key.trim(),
        Value: form.Value,
        Scope: form.Scope,
        Type: form.Type,
      });
      finish(res, "Variable saved");
      if (res?.status === 200) {
        setForm(emptyForm);
        setEditingKey(null);
      }
    } catch (e) {
      toast.error("Change failed");
    }
    setSaving(false);
  };

  const handleRemove = async (key) => {
    setConfirmRemoveKey(null);
    setSaving(true);
    try {
      const res = await envDeleteMasterSystemServer({ Servers: targetIds(), Key: key });
      finish(res, "Variable removed");
      if (editingKey === key) {
        setForm(emptyForm);
        setEditingKey(null);
      }
    } catch (e) {
      toast.error("Change failed");
    }
    setSaving(false);
  };

  const startEdit = (env) => {
    setEditingKey(env.Key);
    setForm({
      Key: env.Key,
      // secrets cannot be read back, so they must be typed in again
      Value: env.Type === "SECRET" ? "" : env.Value || "",
      Scope: env.Scope || "RUN_AND_BUILD_TIME",
      Type: env.Type || "GENERAL",
    });
  };

  const keyValid = /^[A-Za-z_][A-Za-z0-9_]*$/.test(form.Key.trim());
  const canSave = keyValid && !(form.Type === "SECRET" && form.Value === "");
  const keyExists = envList.some((e) => e.Key === form.Key.trim());

  const toggleAlso = (id) =>
    setAlsoServerIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );

  return (
    <AppModal
      open={open}
      onClose={onClose}
      title="Environment Variables"
      subtitle={server ? `${server.Name} (${server.Environment})` : ""}
      icon="ri-key-2-line"
      size="lg"
      loading={saving}
      loadingText="Updating DigitalOcean..."
      onSubmit={handleSave}
      submitLabel={editingKey || keyExists ? "Update variable" : "Add variable"}
      submitDisabled={!canSave}
      cancelLabel="Close"
    >
      <Alert color="warning" className="py-2 small">
        Saving or removing a variable updates the DigitalOcean app spec and
        redeploys every app it is applied to.
      </Alert>

      {error && (
        <Alert color="danger" className="py-2 small">
          {error}
        </Alert>
      )}

      <div className="table-responsive mb-3">
        <Table size="sm" className="align-middle mb-0">
          <thead className="table-light">
            <tr>
              <th>Key</th>
              <th>Value</th>
              <th>Scope</th>
              <th>Type</th>
              <th className="text-end"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="5" className="text-center text-muted py-3">
                  <Spinner size="sm" className="me-2" />
                  Loading variables...
                </td>
              </tr>
            ) : envList.length ? (
              envList.map((env) => (
                <tr key={env.Key}>
                  <td className="font-monospace small">{env.Key}</td>
                  <td
                    className="font-monospace small text-truncate"
                    style={{ maxWidth: 220 }}
                    title={env.Type === "SECRET" ? "" : env.Value}
                  >
                    {env.Type === "SECRET" ? (
                      <span className="text-muted">encrypted</span>
                    ) : (
                      env.Value
                    )}
                  </td>
                  <td className="small">
                    {SCOPES.find((s) => s.value === env.Scope)?.label || env.Scope}
                  </td>
                  <td>
                    <span
                      className={`badge ${
                        env.Type === "SECRET"
                          ? "bg-danger-subtle text-danger"
                          : "bg-secondary-subtle text-secondary"
                      }`}
                    >
                      {env.Type}
                    </span>
                  </td>
                  <td className="text-end text-nowrap">
                    {confirmRemoveKey === env.Key ? (
                      <>
                        <Button
                          color="danger"
                          size="sm"
                          className="me-1"
                          disabled={saving}
                          onClick={() => handleRemove(env.Key)}
                        >
                          Remove
                        </Button>
                        <Button
                          color="light"
                          size="sm"
                          onClick={() => setConfirmRemoveKey(null)}
                        >
                          Keep
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button
                          color="light"
                          size="sm"
                          className="me-1"
                          onClick={() => startEdit(env)}
                        >
                          <i className="ri-pencil-line" />
                        </Button>
                        <Button
                          color="light"
                          size="sm"
                          onClick={() => setConfirmRemoveKey(env.Key)}
                        >
                          <i className="ri-delete-bin-line text-danger" />
                        </Button>
                      </>
                    )}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="5" className="text-center text-muted py-3">
                  No app-level variables.
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>

      {componentEnvCount > 0 && (
        <p className="text-muted small">
          This app also has {componentEnvCount} component-level variable
          {componentEnvCount === 1 ? "" : "s"}, which are not shown or changed
          here.
        </p>
      )}

      <h6 className="mb-2">
        {editingKey ? `Edit ${editingKey}` : "Add a variable"}
        {editingKey && (
          <Button
            color="link"
            size="sm"
            className="ms-2 p-0"
            onClick={() => {
              setEditingKey(null);
              setForm(emptyForm);
            }}
          >
            New instead
          </Button>
        )}
      </h6>
      <Row className="g-2">
        <Col md="6">
          <Label className="form-label small mb-1">Key</Label>
          <Input
            bsSize="sm"
            className="font-monospace"
            value={form.Key}
            disabled={!!editingKey}
            invalid={form.Key !== "" && !keyValid}
            placeholder="MY_NEW_VAR"
            onChange={(e) => setForm({ ...form, Key: e.target.value })}
          />
          {!editingKey && keyExists && (
            <div className="small text-warning mt-1">
              This key exists and will be replaced.
            </div>
          )}
        </Col>
        <Col md="6">
          <Label className="form-label small mb-1">Value</Label>
          <Input
            bsSize="sm"
            className="font-monospace"
            type={form.Type === "SECRET" ? "password" : "text"}
            autoComplete="new-password"
            value={form.Value}
            placeholder={
              editingKey && form.Type === "SECRET" ? "Enter the new secret value" : ""
            }
            onChange={(e) => setForm({ ...form, Value: e.target.value })}
          />
        </Col>
        <Col md="6">
          <Label className="form-label small mb-1">Scope</Label>
          <Input
            type="select"
            bsSize="sm"
            value={form.Scope}
            onChange={(e) => setForm({ ...form, Scope: e.target.value })}
          >
            {SCOPES.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </Input>
        </Col>
        <Col md="6">
          <Label className="form-label small mb-1">Type</Label>
          <Input
            type="select"
            bsSize="sm"
            value={form.Type}
            onChange={(e) => setForm({ ...form, Type: e.target.value })}
          >
            <option value="GENERAL">General (readable)</option>
            <option value="SECRET">Secret (encrypted)</option>
          </Input>
        </Col>
      </Row>

      {otherServers.length > 0 && (
        <div className="mt-3">
          <Label className="form-label small mb-1">
            Also apply saves and removals to
          </Label>
          <div className="d-flex flex-wrap gap-3">
            {otherServers.map((s) => (
              <div className="form-check" key={s.Id}>
                <Input
                  type="checkbox"
                  className="form-check-input"
                  id={`env-also-${s.Id}`}
                  checked={alsoServerIds.includes(s.Id)}
                  onChange={() => toggleAlso(s.Id)}
                />
                <Label className="form-check-label small" for={`env-also-${s.Id}`}>
                  {s.Name}{" "}
                  <span className="text-muted text-uppercase">({s.Environment})</span>
                </Label>
              </div>
            ))}
          </div>
        </div>
      )}

      {results.length > 0 && (
        <ul className="list-unstyled small mt-3 mb-0">
          {results.map((r) => (
            <li key={r.Id} className={Number(r.Success) ? "text-success" : "text-danger"}>
              <i
                className={`me-1 ${
                  Number(r.Success) ? "ri-checkbox-circle-line" : "ri-error-warning-line"
                }`}
              />
              {r.Name || `Server ${r.Id}`}: {r.Message}
            </li>
          ))}
        </ul>
      )}
    </AppModal>
  );
};

export default EnvVarsModal;
